import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Badge, Row, Col } from 'react-bootstrap';
import { BlockEditor } from '../editor/BlockEditor.jsx';
import { docFromJSON, docToHTML, migrateLegacyContent } from '../editor/core/serialize.js';

/**
 * ContentBlockEditor
 *
 * Modal wrapper around the block-first BlockEditor.
 *
 * Props:
 *   show    {boolean}
 *   onHide  {Function}
 *   content {object|null}  – existing content block (null = new)
 *   onSave  {Function}     – called with the serialized content block object
 *
 * Saved data format:
 *   {
 *     id:            string,
 *     title:         string,
 *     contentType:   'doc',
 *     doc:           object,   // normalized block tree
 *     content:       string,   // HTML serialization (backward compat)
 *     showInInitial: boolean,
 *     showInReview:  boolean,
 *     tags:          string[],
 *   }
 */
export function ContentBlockEditor({ show, onHide, content, onSave }) {
  const [title, setTitle] = useState('');
  const [showInInitial, setShowInInitial] = useState(true);
  const [showInReview, setShowInReview] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const [docKey, setDocKey] = useState(0); // bump to force BlockEditor remount
  const [initialDoc, setInitialDoc] = useState(null);

  // Current doc is held inside BlockEditor; we mirror it via onSave/onChange
  // so the outer "Save Content" button always persists the latest edits.
  const [pendingDoc, setPendingDoc] = useState(null);

  useEffect(() => {
    if (!show) return;
    if (content) {
      const legacyType = content.contentType;
      const isDoc = legacyType === 'doc';

      let doc;
      if (isDoc && content.doc) {
        doc = docFromJSON(content.doc);
      } else if (content.content || content.contentType) {
        doc = migrateLegacyContent({
          contentType: legacyType,
          content: content.content || '',
        });
      } else {
        doc = null;
      }

      setTitle(content.title || '');
      setShowInInitial(content.showInInitial !== false);
      setShowInReview(content.showInReview === true);
      setTagsInput(Array.isArray(content.tags) ? content.tags.join(', ') : '');
      setInitialDoc(doc);
      setPendingDoc(doc);
    } else {
      setTitle('');
      setShowInInitial(true);
      setShowInReview(false);
      setTagsInput('');
      setInitialDoc(null);
      setPendingDoc(null);
    }
    setDocKey((k) => k + 1);
  }, [content, show]);

  const handleDocSave = (doc) => {
    setPendingDoc(doc);
  };

  const handleSubmit = () => {
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }
    const doc = pendingDoc;
    const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
    const html = doc ? docToHTML(doc) : '';

    onSave({
      id: content?.id || `content-${Date.now()}`,
      title: title.trim(),
      contentType: 'doc',
      doc,
      content: html,
      showInInitial,
      showInReview,
      tags,
    });
  };

  const parsedTags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="editor-modal-dialog">
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-6">
          {content ? '✏️ Edit Content Block' : '📝 New Content Block'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 d-flex flex-column" style={{ height: '80vh' }}>
        {/* Meta row (title, visibility, tags) */}
        <div className="border-bottom px-3 py-2 bg-light flex-shrink-0">
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <Form.Label className="fw-semibold small mb-1">Title</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Content block title"
                required
              />
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold small mb-1">Visibility</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="checkbox"
                  id="cbe-showInInitial"
                  label="Initial"
                  checked={showInInitial}
                  onChange={(e) => setShowInInitial(e.target.checked)}
                  className="small"
                />
                <Form.Check
                  type="checkbox"
                  id="cbe-showInReview"
                  label="Review"
                  checked={showInReview}
                  onChange={(e) => setShowInReview(e.target.checked)}
                  className="small"
                />
              </div>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold small mb-1">
                Tags <span className="text-muted fw-normal">(comma-separated)</span>
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g. variables, loops"
              />
              {parsedTags.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {parsedTags.map((tag) => (
                    <Badge key={tag} bg="secondary" style={{ fontSize: '0.65rem' }}>{tag}</Badge>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </div>

        {/* Block editor (fills remaining space) */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <BlockEditor
            key={docKey}
            initialDoc={initialDoc}
            title=""
            onSave={handleDocSave}
            onChange={handleDocSave}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSubmit}>Save Content</Button>
      </Modal.Footer>
    </Modal>
  );
}
